/**
 * Assigned Service
 *
 * Fetch API implementation for assigned experiences by class and status.
 */

import { toRequiredNumber } from '../../../services/Utilities';
const API_SERVER = process.env.API_SERVER || '';

export type AssignedExperienceStatus = 'active' | 'pending' | 'past';
export interface AssignedExperienceFilter {
  schools: number[];
  teachers: number[];
  courses: string[];
}

export interface SubscriptionExperienceFiltersFilter {
  teachers: number[];
  school_ids: number[];
  collection_uuids: string[];
}
const VALID_ASSIGNED_EXPERIENCE_STATUSES: AssignedExperienceStatus[] = ['active', 'pending', 'past'];

function validateAssignedExperienceStatus(status: AssignedExperienceStatus): AssignedExperienceStatus {
  if (!VALID_ASSIGNED_EXPERIENCE_STATUSES.includes(status)) {
    throw new Error(`status must be one of: ${VALID_ASSIGNED_EXPERIENCE_STATUSES.join(', ')}`);
  }
  return status;
}

function addIncreaseLimit(url: string, increaseLimit: number): string {
  return increaseLimit > 0
    ? `${url}?limit=${increaseLimit}`
    : url;
}

async function fetchExperiencesByStatus(
  baseUrl: string,
  status: AssignedExperienceStatus,
  increaseLimit: number,
  failureMessage: string,
  requestOptions: Pick<RequestInit, 'method' | 'body'> = {}
): Promise<unknown[]> {
  validateAssignedExperienceStatus(status);
  const response = await fetch(
    addIncreaseLimit(`${baseUrl}/${status}`, increaseLimit),
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      ...requestOptions,
    }
  );

  if (!response.ok) {
    throw new Error(`${failureMessage}: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

export class AssignedService {
  static async getClassExperiencesByStatus(
    classId: number,
    status: AssignedExperienceStatus,
    increaseLimit: number = 0
  ): Promise<unknown[]> {
    const normalizedClassId = toRequiredNumber(classId, 'classId');
    if (normalizedClassId <= 0) {
      throw new Error('classId must be a positive number');
    }
    return fetchExperiencesByStatus(
      `${API_SERVER}/experiences/classes/${normalizedClassId}`,
      status,
      increaseLimit,
      'Failed to fetch class experiences'
    );
  }

  static async getAllExperiencesByStatus(
    status: AssignedExperienceStatus,
    increaseLimit: number = 0
  ): Promise<unknown[]> {
    return fetchExperiencesByStatus(
      `${API_SERVER}/experiences/all`,
      status,
      increaseLimit,
      'Failed to fetch all experiences'
    );
  }

  static async getSubscriptionExperiencesByStatus(
    subscriptionId: number,
    status: AssignedExperienceStatus,
    filter?: AssignedExperienceFilter,
    increaseLimit: number = 0
  ): Promise<unknown[]> {
    const normalizedSubscriptionId = toRequiredNumber(subscriptionId, 'subscriptionId');
    if (normalizedSubscriptionId <= 0) {
      throw new Error('subscriptionId must be a positive number');
    }
    const shouldSendFilter = !!filter
      && (filter.schools.length > 0 || filter.teachers.length > 0 || filter.courses.length > 0);

    return fetchExperiencesByStatus(
      `${API_SERVER}/admin/subscriptions/${normalizedSubscriptionId}/experiences`,
      status,
      increaseLimit,
      'Failed to fetch subscription experiences',
      shouldSendFilter
        ? {
            method: 'POST',
            body: JSON.stringify({ filter }),
          }
        : undefined
    );
  }

  static async getSubscriptionExperienceFilters(
    subscriptionId: number,
    filter?: SubscriptionExperienceFiltersFilter
  ): Promise<unknown> {
    const normalizedSubscriptionId = toRequiredNumber(subscriptionId, 'subscriptionId');
    if (normalizedSubscriptionId <= 0) {
      throw new Error('subscriptionId must be a positive number');
    }
    const baseUrl = `${API_SERVER}/subscriptions/admin/subscriptions/${normalizedSubscriptionId}/experiencefilters`;
    const shouldSendFilter = !!filter
      && (filter.school_ids.length > 0 || filter.teachers.length > 0 || filter.collection_uuids.length > 0);
    const url = shouldSendFilter
      ? `${baseUrl}?filter=${encodeURIComponent(JSON.stringify(filter))}`
      : baseUrl;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch subscription experience filters: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }
}

export default AssignedService;
